import { useNavigate } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import { Button, Label, Message, Table } from 'semantic-ui-react';
import { RESPONSE_STATUS } from '../../../../constnats/general';
import { ROLES } from '../../../../constnats/user';
import useTasks from '../../../../hooks/task/useTasks';
import useModalState from '../../../../hooks/useModalState';
import useUsersMap from '../../../../hooks/useUsersMap';
import {
    ListTasksFilterWithoutPage,
    TaskCreationSchemaType,
    TaskInfoType,
    TaskUpdateSchemaType,
} from '../../../../schemas/task.schemas';
import Alert from '../../../../utils/alert';
import TasksRepo from '../../../../utils/repository/tasksRepo/tasksRepo';
import Loader from '../../../loader';
import Pagination from '../../../pagination';
import TaskDueDate from '../taskDueDate';
import TaskModal from '../taskModal';

type TasksTablePropsType = {
    taskCreationEnabled?: boolean;
    filters?: ListTasksFilterWithoutPage;
};

function TasksTable({ taskCreationEnabled = false, filters }: TasksTablePropsType) {
    const navigate = useNavigate();
    const { tasks, loading: loadingTasks, refetch, page, setPage, totalPages } = useTasks(filters);
    const { usersMap, loading: loadingUsers } = useUsersMap({ includeDisabled: false, userRoles: [ROLES.EMPLOYEE] });
    const taskModalState = useModalState();

    const handleConfirm = (data: TaskCreationSchemaType & TaskUpdateSchemaType) => {
        const taskData: TaskCreationSchemaType = {
            assignedUserId: data.assignedUserId,
            createdById: data.createdById,
            description: data.description,
            dueDate: data.dueDate,
            priority: data.priority,
            title: data.title,
        };

        TasksRepo.createTask(taskData, (response) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Task Created Successfully!');
                refetch();
            } else {
                Alert.warn(response.message);
            }
        });
        taskModalState.closeModal();
    };

    const navigateToTask = (task: TaskInfoType) => {
        navigate(`/admin/tasks/${task.id}`);
    };

    const getUserName = (task: TaskInfoType): string => {
        if (!task.assignedUserId) {
            return '';
        }

        if (usersMap[task.assignedUserId]) {
            return usersMap[task.assignedUserId].fullName;
        } else {
            return 'User not found';
        }
    };

    if (loadingTasks || loadingUsers) {
        return <Loader />;
    }

    if (tasks.length === 0) {
        return <Message>There are currently no tasks</Message>;
    }

    return (
        <Fragment>
            <Table striped>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Id</Table.HeaderCell>
                        <Table.HeaderCell>Title</Table.HeaderCell>
                        <Table.HeaderCell>Status</Table.HeaderCell>
                        <Table.HeaderCell>Priority</Table.HeaderCell>
                        <Table.HeaderCell>Assignee</Table.HeaderCell>
                        <Table.HeaderCell>Due Date</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {tasks.map((task) => {
                        return (
                            <Table.Row onClick={() => navigateToTask(task)} className="cursorPointer" key={task.id}>
                                <Table.Cell>
                                    <Label>{task.id}</Label>
                                </Table.Cell>
                                <Table.Cell>{task.title}</Table.Cell>
                                <Table.Cell>{task.status}</Table.Cell>
                                <Table.Cell>{task.priority}</Table.Cell>
                                <Table.Cell>{getUserName(task)}</Table.Cell>
                                <Table.Cell>
                                    <TaskDueDate date={task.dueDate} status={task.status}/>
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
            <div className="flex full-width">
                <Pagination page={page} totalPages={totalPages} setPage={setPage} />
                {taskCreationEnabled && (
                    <Button className="ml-auto" onClick={taskModalState.openModal}>
                        Create Task
                    </Button>
                )}
            </div>
            <TaskModal
                open={taskModalState.open}
                onCancel={taskModalState.closeModal}
                onSave={handleConfirm}
                users={Object.values(usersMap)}
            />
        </Fragment>
    );
}

export default TasksTable;
